// Automated rule guardrails: the single source of truth for deciding whether a
// prompt breaks one of the rules published at https://eropenpal.com/rules.
//
// This file is CommonJS on purpose, and a byte-identical copy lives at
// functions/utils/promptRules.js. The Cloud Functions package is deployed from
// functions/ alone and cannot import out of src/, so the copy is mechanical:
// run `npm run sync-prompt-rules` after touching this file.
// tests/prompt-rules.spec.js fails the build if the two ever drift.
//
// Layering - the client check is NOT the guardrail:
//   - src/utils/checkPromptObjectBefore.js calls this on the PUBLISH path only,
//     so an author gets an inline error instead of a silent takedown later.
//     Drafts are deliberately exempt: you can save a work in progress that is
//     not clean yet.
//   - functions/promptModerationFunctions.js calls it again on every prompt
//     write. THAT is the enforcement point - anyone can talk to Firestore
//     directly and skip the client entirely.
//
// Two severities:
//   block  - refuse the publish outright. Reserved for patterns precise enough
//            that a false positive is very unlikely, because a wrong block
//            silently censors a paying adult user.
//   review - publish normally, record a verdict for a human moderator.
// When in doubt the correct tier is review.
//
// This is an adult site: explicit sexual content between adults is the product
// and is never itself a violation. Every pattern below targets the specific
// rule it cites, not explicitness. The table was tuned against a corpus of
// legitimate explicit writing - consensual degradation, ageplay/littlespace,
// findom fantasy, race-play and CNC all read as clean. The regression corpus
// lives in tests/prompt-rules.spec.js; extend it whenever you touch a pattern.

// Bump when the table or the normaliser changes. Stored on each prompt as
// moderation.version so the Cloud Function can tell "checked by an older
// engine" from "never checked" and re-scan only what needs it.
const RULES_ENGINE_VERSION = 3;

// Longest match echoed back to the author, so an error can say what to reword
// without quoting a paragraph back at them.
const MATCH_ECHO_LIMIT = 48;

// The five published rules, keyed by the category each pattern declares.
const RULES = {
  harassment: {
    number: 1,
    title: 'Be respectful'
  },
  underage: {
    number: 2,
    title: '18+ ONLY, No Underage Roles Offered/Requested'
  },
  money: {
    number: 3,
    title: 'No Offering or Soliciting Money/Compensation'
  },
  'image-link': {
    number: 4,
    title: 'No Inappropriate or Illegal Image/Link'
  },
  copying: {
    number: 5,
    title: "Don't copy someone else's writing"
  }
};

// --------------------------------------------------------------- normalising
//
// Homoglyph substitution is the single highest-value bypass to close: Cyrillic
// "аge 15" defeats every pattern in the table for the cost of one keystroke.
// Unicode NFKC folds fullwidth, mathematical and compatibility forms for free;
// this map covers the confusables NFKC deliberately leaves alone because they
// are genuinely distinct letters in their own scripts.
const CONFUSABLES = {
  а: 'a', в: 'b', с: 'c', ԁ: 'd', е: 'e', һ: 'h', і: 'i', ј: 'j', к: 'k',
  ӏ: 'l', м: 'm', н: 'h', о: 'o', р: 'p', ԛ: 'q', ѕ: 's', т: 't', у: 'y',
  х: 'x', А: 'A', В: 'B', Е: 'E', К: 'K', М: 'M', Н: 'H', О: 'O', Р: 'P',
  С: 'C', Т: 'T', У: 'Y', Х: 'X',
  α: 'a', β: 'b', ε: 'e', ι: 'i', κ: 'k', ν: 'v', ο: 'o', ρ: 'p', τ: 't',
  υ: 'u', χ: 'x', Α: 'A', Β: 'B', Ε: 'E', Ζ: 'Z', Η: 'H', Ι: 'I', Κ: 'K',
  Μ: 'M', Ν: 'N', Ο: 'O', Ρ: 'P', Τ: 'T', Υ: 'Y', Χ: 'X'
};

const CONFUSABLE_RE = new RegExp(`[${Object.keys(CONFUSABLES).join('')}]`, 'g');

// Zero-width and invisible characters, used to split a banned word in half
// ("1<ZWJ>5yo") without changing how it looks. Also soft hyphen and the
// combining marks left over after NFKC, which is how zalgo noise survives.
const INVISIBLE_RE = /[\u00ad\u180e\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g;
const COMBINING_RE = /[\u0300-\u036f]/g;

// Fold typographic variants onto their ASCII originals so a pattern written
// with a straight quote still matches text pasted out of a word processor.
const PUNCTUATION_FOLD = [
  [/[‘’‚‛′]/g, "'"],
  [/[“”„‟″]/g, '"'],
  [/[‐-―−]/g, '-'],
  [/…/g, '...']
];

// Strip accents by decomposing first, so "\u00e0ged 15" and "l\u00f3licon" cannot walk past
// patterns written in plain ASCII. This is applied as a SECOND view of the text
// rather than folded into normalizeForMatching, because a few patterns require
// an accent to mean anything: coll\u00e8ge and lyc\u00e9e are sub-18 school years, while
// unaccented "college" is an adult. Stripping unconditionally would either
// break those patterns or make them fire on ordinary English.
const stripDiacritics = value => value.normalize('NFD').replace(COMBINING_RE, '');

// Everything the patterns run against goes through here first. Order matters:
// NFKC before the confusable map (so fullwidth Cyrillic folds to plain
// Cyrillic first), invisibles before anything that relies on adjacency.
const normalizeForMatching = value => {
  if (typeof value !== 'string' || value === '') return '';
  let out = value.normalize('NFKC');
  out = out.replace(INVISIBLE_RE, '');
  out = out.replace(COMBINING_RE, '');
  out = out.replace(CONFUSABLE_RE, ch => CONFUSABLES[ch]);
  PUNCTUATION_FOLD.forEach(pair => {
    out = out.replace(pair[0], pair[1]);
  });
  // collapse runs of whitespace but keep newlines meaningful for \b handling
  return out.replace(/[^\S\n]+/g, ' ');
};

const PATTERN_TABLE = [
  // Rule 2, gap #9. "I am <18>" stated in the four non-English languages
  // most common in this corpus. Each is as unambiguous as its English
  // equivalent.
  {
    id: 'underage-age-foreign-language',
    category: 'underage',
    severity: 'block',
    source: "\\bteng[oa]\\s+(?:1[0-7]|[3-9])\\s+a[\\u00f1n]os\\b|\\bj\\s?['\\u2019]?\\s?ai\\s+(?:1[0-7]|[3-9])\\s+ans\\b|\\bich\\s+bin\\s+(?:1[0-7]|[3-9])\\s+jahre?\\b|\\btenho\\s+(?:1[0-7]|[3-9])\\s+anos\\b|\\bho\\s+(?:1[0-7]|[3-9])\\s+anni\\b"
  },
  // Rule 2. Labelled age fields common in penpal headers: 'age 15', 'aged
  // 16', 'age: 14', 'ages 12 and up'.
  {
    id: 'underage-age-label-number',
    category: 'underage',
    severity: 'block',
    source: "\\baged\\s{0,2}(?:1[0-7]|[3-9])\\b(?!\\s{0,3}(?:\\w+\\s+){0,2}(?:years?\\s+in|months?\\s+in|in\\s+oak|in\\s+barrels?|in\\s+casks?|head\\s?space|little\\s?space|in\\s+little))|\\bages?\\s{0,2}[:=]\\s{0,2}(?:1[0-7]|[3-9])\\b|\\bages?\\s{0,2}(?:1[0-7]|[3-9])\\s{0,2}(?:[,\\/|]|\\b(?:f|m|nb|fem|femme|female|male|masc|girl|boy|sub|dom|switch)\\b)|\\bages?\\s+(?:1[0-7]|[3-9])\\s+and\\s+(?:up|under|younger|older|below|above)\\b"
  },
  // Rule 2. Unbracketed a/s/l shorthand: '17f', 'f16', '16/f/usa', 'm/17'.
  {
    id: 'underage-asl-shorthand',
    category: 'underage',
    severity: 'block',
    source: "(?<![\\w\\-$£€])(?:1[0-7]\\s{0,2}\\/\\s{0,2}[mf]|[mf]\\s{0,2}\\/\\s{0,2}1[0-7]|1[0-7][mf]|[mf]1[0-7])(?![\\w\\-])(?!\\s{0,2}(?:rifle|carbine|jet|fighter|falcon|assault|million|meters?|metres?|miles?|minutes?|\\d{1,2}\\s?s\\b|long\\b|clip|video|audio|recording|drive|walk|away))"
  },
  // Rule 2. Bracketed age/sex title tags, the standard penpal post header:
  // '[M16]', '[16F]', '(17f)'.
  {
    id: 'underage-asl-tag-bracketed',
    category: 'underage',
    severity: 'block',
    source: "[\\[\\(]\\s{0,2}(?:1[0-7]\\s{0,2}[mfa]|[mfa]\\s{0,2}1[0-7])\\s{0,2}[\\]\\)]"
  },
  // Rule 4 (no images of anyone under 18 posted, offered or requested) and
  // rule 2. Direct requests for or offers of sexual imagery of minors.
  {
    id: 'underage-csam-terms',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|won['’]?t|wont|nothing|nobody|none|without|against|hate|report|reports|reporting|block|blocks|blocking|ban|bans|banning|avoid|refuse|refusing)\\b(?:\\W+\\w+){0,2}\\W)(?:\\b(?:child|children|kid|kids|kiddie|kiddy|underage|pre[\\s-]?teen|toddler|infant)[\\s-]{0,2}(?:p[o0]rn\\w*|nudes?|nudity|erotica)\\b|\\bcs[ae]m\\b|\\bchild\\s{0,2}sex(?:ual)?\\s{0,2}(?:abuse|content|material|images?)\\b)"
  },
  // Rule 2. Bare self-declaration of a minor age with no unit word: "i'm
  // 17", 'im 15', 'I am 16', "I'm only 13".
  {
    id: 'underage-first-person-numeric-age',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:i\\s?am|i\\s?['’]?m)\\s{1,3}(?:only\\s+|just\\s+|still\\s+|almost\\s+|nearly\\s+)?(?:1[0-7])\\b(?=\\s{0,2}(?:[,.!?;:)\\/]|$|and\\b|but\\b|so\\b|tho\\b|though\\b|f\\b|m\\b|nb\\b|fem(?:ale)?\\b|male\\b|yo\\b|yrs?\\b|years?\\s+old\\b|looking\\b|seeking\\b|searching\\b|here\\b|now\\b|today\\b|already\\b|turning\\b|soon\\b))"
  },
  // Rule 2. A named grade-level person. 1st through 11th graders are 6-17
  // years old, so the role is necessarily under 18.
  {
    id: 'underage-grader-ordinal',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:(?:1[01]|[1-9])(?:st|nd|rd|th)|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh)[\\s\\-]{0,2}graders?\\b"
  },
  // Rule 2. High-school freshman/sophomore/junior are 14-17 by definition,
  // unlike 'high school senior' (17-18) or 'college freshman' (18+), which
  // are left ambiguous.
  {
    id: 'underage-high-school-underclassman',
    category: 'underage',
    severity: 'block',
    source: "\\bhigh[\\s\\-]?school\\s+(?:fresh(?:man|men)|sophomore|junior)\\b(?!\\s+(?:varsity|year|prom|dance|class|reunion|teacher|coach))|\\b(?:fresh(?:man|men)|sophomore)\\s+(?:in|at)\\s+(?:high[\\s\\-]?school|hs)\\b"
  },
  // Rule 2. Terms of art that denote sexualised minors and have no adult
  // meaning, plus their standard leetspeak obfuscations ('l0li', '1oli',
  // 'lol1').
  {
    id: 'underage-minor-fetish-terms',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:lolicon|shotacon|toddlercon|cubcon|cub\\s?p[o0]rn|jail\\s?baits?|(?!\\d{4}\\b)[l1][o0][l1][i1])\\b"
  },
  // Rule 2. 'Almost 18' means 17. Catches the not-yet-adult framings that
  // mention only the legal number: "I'm not 18 yet", 'almost 18', 'turning
  // 18 next month'.
  {
    id: 'underage-not-yet-eighteen',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:not|isn['’]?t|aren['’]?t|ain['’]?t|almost|nearly)\\s+(?:quite\\s+|even\\s+|yet\\s+)?(?:18|eighteen)\\b(?!\\s{0,3}(?:\\+|inch|in\\b|cm|mm|\"|'|years?|yrs?|months?|weeks?|days?|hrs?|hours?|minutes?|seconds?|people|guys|men|women|partners?|stone|lbs?|kgs?|miles?|km|degrees?|percent|%|times?|holes?|anymore|any\\s+more|any\\s+longer|forever|again))|\\bturn(?:ing|s)?\\s+(?:18|eighteen)\\s+(?:in(?!\\s+order)|next|this)\\b|\\b(?:18|eighteen)\\s+in\\s+(?:a\\s+|\\d{1,2}\\s+)?(?:few\\s+|couple\\s+(?:of\\s+)?)?(?:days?|weeks?|months?)\\b"
  },
  // Rule 2. The single most common way a sub-18 age is stated: '16 year
  // old', '14-year-old', '17yr old', '12 years of age'. Blocks 3-17 only.
  {
    id: 'underage-numeric-age-years-old',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:1[0-7]|[3-9])[\\s.\\-]{0,3}(?:years?|yrs?)[\\s.\\-]{0,3}(?:old|of\\s{1,3}age)\\b(?=\\s{0,3}(?:$|[,.!?;:)\\]]|and\\b|but\\b|who\\b|looking\\b|seeking\\b|wanting\\b|here\\b|in\\s+this\\b|f\\b|m\\b|girls?\\b|boys?\\b|guys?\\b|gal\\b|female\\b|male\\b|virgins?\\b|sluts?\\b|whores?\\b|subs?\\b|doms?\\b|brats?\\b|princess\\b|students?\\b|runaways?\\b|babysitters?\\b|cheerleaders?\\b|twinks?\\b|kids?\\b|child\\b|teens?\\b|version\\b|me\\b|i\\b|she\\b|he\\b|they\\b))"
  },
  // Rule 2. The dominant penpal shorthand: '16yo', '15 y/o', '14 y.o.', '17
  // y o'.
  {
    id: 'underage-numeric-age-yo-abbrev',
    category: 'underage',
    severity: 'block',
    source: "(?<![\\w\\/.$£€])(?:1[0-7]|[3-9])\\s{0,2}y[\\s.\\/\\-]{0,2}o\\b(?!\\s{0,2}(?:son|daughter|kid|kiddo|child|children|nephew|niece|brother|sister|sibling|grand(?:son|daughter|child)|dog|cat|puppy|kitten|pup|labrador|retriever|terrier|horse|pony)\\b)"
  },
  // Rule 2. A PERSON is explicitly put at an age under 18 - "we were six
  // years old", "I was 15 years old". The sibling pattern guards itself with
  // a trailing person-noun allowlist so that "a 5 year old dry spell" stays
  // publishable, but that guard also lets through any age simile with an
  // unusual continuation ("like we were five years old again"). A pronoun
  // subject plus a copula plus an age IS the person noun, so no trailing
  // guard is needed and none is wanted: rule 2 bans INCLUDING an under-18
  // role, however it is framed.
  {
    id: 'underage-person-stated-age',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:we|i|you|they|he|she|both\\s+of\\s+us)\\s+(?:were|was|are|am|is)\\s+(?:only\\s+|just\\s+|about\\s+|around\\s+|like\\s+|still\\s+)?(?:1[0-7]|[3-9]|seventeen|sixteen|fifteen|fourteen|thirteen|twelve|eleven|ten|nine|eight|seven|six|five|four|three)[\\s.\\-]{0,3}(?:years?|yrs?)[\\s.\\-]{0,3}old\\b"
  },
  // Rule 2. Words whose literal definition is 'below sexual maturity' - no
  // adult referent exists.
  {
    id: 'underage-prepubescent',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:pre[\\s.\\-]?pubescent|pre[\\s.\\-]?pubertal|prepubes\\w*|pre[\\s.\\-]?teens?)\\b"
  },
  // Rule 2. Negated-puberty phrasing describes a child without ever stating
  // a number, so it evades every numeric pattern.
  {
    id: 'underage-puberty-not-reached',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\bi\\s)(?<!\\bi['’]ve\\s)(?<!\\bwe\\s)\\b(?:hasn['’]?t|haven['’]?t|has\\s+not|have\\s+not|isn['’]?t|not|never)\\s+(?:even\\s+|yet\\s+|quite\\s+)?(?:hit|reached|started|gone\\s+through|been\\s+through|finished|entered)\\s+puberty\\b"
  },
  // Rule 2. A sub-high-school institution attached to a sexualised person
  // noun. Every US grade below 9th is under 15, so the character is
  // necessarily a minor.
  {
    id: 'underage-school-level-minor-noun',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\b(?:teach|teaches|teaching|taught|tutor|tutors|tutoring|my|our)\\s(?:\\w+\\s){0,2})\\b(?:(?:middle|elementary|grade|primary|grammar|nursery|pre)[\\s\\-]?school|jr\\.?[\\s\\-]?high(?:[\\s\\-]?school)?|junior[\\s\\-]?high(?:[\\s\\-]?school)?|kindergarten)[\\s\\-]{0,2}(?:girls?|boys?|cheerleaders?|virgins?|sluts?|whores?|twinks?|brats?|princess(?:es)?)\\b"
  },
  // Rule 2. Non-numeric self-identification as a minor: "I'm a minor", 'im
  // underage', "I'm under 18", "I am not 18 yet".
  {
    id: 'underage-self-identified-minor',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:i\\s?am|i\\s?['’]?m)\\s{1,3}(?:an?\\s+)?(?:still\\s+|only\\s+|legally\\s+)?(?:an?\\s+)?(?:minor|underage|under[\\s-]?18|under\\s+the\\s+age|not\\s+(?:yet\\s+)?(?:18|eighteen|legal|of\\s+age))\\b(?!\\s{0,2}(?:inconvenience|inconvenient|detail|issue|character|role|adjustment|change|setback|key|chord|league|leaguer|deity|prophet|celebrity|player|point|months?|weeks?|years?|days?|hours?|stone|inch|inches|cm|kg|kgs?|lbs?|anymore|any\\s+more|any\\s+longer|to\\s+drink|to\\s+buy|to\\s+drive|to\\s+gamble|to\\s+rent|for\\s+alcohol|in\\s+my\\s+state|in\\s+my\\s+country))"
  },
  // Rule 2. An explicit sexual verb taking a minor as its direct object -
  // the clearest possible statement of the prohibited scenario.
  {
    id: 'underage-sex-act-with-minor',
    category: 'underage',
    severity: 'block',
    source: "\\b(?:fuck(?:ing|s|ed)?|molest(?:ing|s|ed)?|rap(?:e|ing|es|ed)|screw(?:ing|s|ed)?|grope(?:d|s)?|deflower(?:ing|ed)?)\\s+(?:an?\\s+|the\\s+|my\\s+|some\\s+|your\\s+)?(?:minors?|kids?|child|children|toddlers?|pre[\\s-]?teens?|middle[\\s-]?schoolers?|8th\\s+graders?)\\b(?!['’])(?!\\s{0,2}(?:these|those|nowadays|today|again\\s+with))"
  },
  // Rule 2, the 'request/offer' half: actively soliciting a minor partner,
  // or advertising that minors may reply.
  {
    id: 'underage-solicitation-of-minors',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|won['’]?t|wont|nothing|nobody|none|without|against|hate|report|reports|reporting|block|blocks|blocking|ban|bans|banning|avoid|refuse|refusing)\\b(?:\\W+\\w+){0,3}\\W)\\b(?:looking\\s+for|look\\s+for|seeking|seek|want|wanting|prefer|into|need|craving|after)\\s+(?:an?\\s+)?(?:young(?:er)?\\s+)?(?:minors?|pre[\\s-]?teens?|middle[\\s-]?schoolers?|grade[\\s-]?schoolers?|underage\\w*|jail\\s?bait)\\b(?!\\s{0,2}(?:detail|issue|inconvenience|change|edit|tweak|adjustment|role|character|key|setback))|(?<!\\b(?:no|not|never|zero|nobody|none)\\b(?:\\W+\\w+){0,1}\\W)\\bminors?\\s+(?:welcome|only|ok(?:ay)?|preferred|encouraged)\\b"
  },
  // Rule 2. Spelled-out evasion of the numeric filter: 'sixteen years old',
  // 'a fourteen-year-old'.
  {
    id: 'underage-spelled-age-years-old',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\b(?:twenty|thirty|forty|fourty|fifty|sixty|seventy|eighty|ninety|hundred|thousand|million|and)[\\s-])\\b(?:seventeen|sixteen|fifteen|fourteen|thirteen|twelve|eleven|ten|nine|eight|seven|six|five|four|three)[\\s.\\-]{0,3}(?:years?|yrs?)[\\s.\\-]{0,3}(?:old|of\\s{1,3}age)\\b(?=\\s{0,3}(?:$|[,.!?;:)\\]]|and\\b|but\\b|who\\b|looking\\b|seeking\\b|wanting\\b|here\\b|in\\s+this\\b|f\\b|m\\b|girls?\\b|boys?\\b|guys?\\b|gal\\b|female\\b|male\\b|virgins?\\b|sluts?\\b|whores?\\b|subs?\\b|doms?\\b|brats?\\b|princess\\b|students?\\b|runaways?\\b|babysitters?\\b|cheerleaders?\\b|twinks?\\b|kids?\\b|child\\b|teens?\\b|version\\b|me\\b|i\\b|she\\b|he\\b|they\\b))"
  },
  // Rule 2. 'Tween' denotes ages roughly 9-12; bound to a person or role
  // noun there is no innocent reading.
  {
    id: 'underage-tween-person',
    category: 'underage',
    severity: 'block',
    source: "\\btweens?\\s{0,2}(?:girls?|boys?|kids?|child|children|body|bodies|role|roles|character|slut|sub)\\b"
  },
  // Rule 2. An explicit upper bound below the legal floor: 'under 16',
  // 'under the age of 17'.
  {
    id: 'underage-under-numeric-age',
    category: 'underage',
    severity: 'block',
    source: "(?<!\\bin\\s)(?<!\\bfinish\\s)(?<!\\bcum\\s)\\bunder\\s+(?:the\\s+age\\s+of\\s+)?(?:1[0-7])\\b(?=\\s{0,3}(?:$|[,.!?;:)\\]]|only\\b|please\\b|and\\b|or\\b|dm\\b|pm\\b|msg\\b|hmu\\b|message\\b|welcome\\b|preferred\\b|allowed\\b|ok\\b|okay\\b|here\\b|is\\b|are\\b|f\\b|m\\b|girls?\\b|boys?\\b|guys?\\b|kids?\\b|teens?\\b|subs?\\b|doms?\\b|in\\s+(?:this|the|my|our)\\b|for\\s+(?:this|the)\\b))"
  },
  // Rule 2, ambiguous and NOT itself a violation. 'Barely legal' and 'just
  // turned 18' assert the user IS 18+, which is permitted here.
  {
    id: 'underage-barely-legal',
    category: 'underage',
    severity: 'review',
    source: "\\bbarely[\\s\\-]?legal\\b|\\bjust\\s+(?:turned\\s+)?(?:18|eighteen)\\b|\\bnewly[\\s\\-]?legal\\b|\\bfresh(?:ly)?\\s+18\\b"
  },
  // Rule 2, recall companion to underage-first-person-numeric-age. Catches
  // 'im 15 curious' and other self-declared minor ages whose continuation is
  // not on the block-tier allowlist.
  {
    id: 'underage-first-person-numeric-age-loose',
    category: 'underage',
    severity: 'review',
    source: "\\b(?:i\\s?am|i\\s?['’]?m)\\s{1,3}(?:only\\s+|just\\s+|still\\s+|almost\\s+|nearly\\s+)?(?:1[0-7])\\b(?!\\s{0,2}(?:\\+|inch|in\\b|cm|mm|ft|feet|foot|lbs?|pounds?|kgs?|kilos?|stone|st\\b|min|mins|minutes?|hrs?|hours?|days?|weeks?|months?|years?|yrs?|miles?|km|degrees?|percent|%|\\d|:|\\/|out\\s+of|kinds?|sorts?|types?|levels?|shades?|chapters?|pages?|paragraphs?|lines?|words?|posts?|prompts?|messages?|replies|comments?|drinks?|beers?|shots?|timezones?|zones?|sizes?|times?))"
  },
  // Rule 2, ambiguous. In furry contexts 'cub' denotes a child character,
  // but in gay contexts it is a legitimate adult body-type identity, so this
  // is queued rather than refused.
  {
    id: 'underage-furry-cub',
    category: 'underage',
    severity: 'review',
    source: "\\bcub\\s{0,2}(?:rp|roleplay|play|art|content)\\b"
  },
  // Rule 2, ambiguous. A grade level as a setting ('8th grade au', 'grade
  // 6') rather than as a person; also covers 12th grade, which straddles 18.
  {
    id: 'underage-grade-level',
    category: 'underage',
    severity: 'review',
    source: "(?<!\\bteach(?:es|ing)?\\s)\\b(?:(?:1[0-2]|[1-9])(?:st|nd|rd|th)|first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|eleventh|twelfth)[\\s\\-]{0,2}grade\\b(?!\\s{0,2}(?:teacher|teaching|instructor|professor|level|point|average|papers?|curriculum|rating|material|quality|stuff))|\\bgrade\\s{0,2}(?:1[0-2]|[1-9])\\b"
  },
  // Rule 2, ambiguous. A high-school setting spans 14-18 and is also a
  // legitimate adult nostalgia/reunion/teacher framing, so it is queued
  // rather than refused.
  {
    id: 'underage-high-school',
    category: 'underage',
    severity: 'review',
    source: "\\bhigh[\\s\\-]?school(?:er|ers)?\\b|\\bhs\\s+(?:student|girl|boy|senior|junior)\\b"
  },
  // Rule 2, ambiguous. Props and settings that only exist in a child or
  // adolescent's daily life; individually weak, collectively a strong signal
  // for a human to look at.
  {
    id: 'underage-juvenile-context',
    category: 'underage',
    severity: 'review',
    source: "\\b(?:homeroom|recess|report\\s?cards?|permission\\s+slips?|school\\s?bus|hall\\s?pass|jungle\\s?gym|training\\s+bra|show\\s+and\\s+tell)\\b"
  },
  // Rule 2, ambiguous. Minor-coded appearance without a stated age ('looks
  // 14', 'could pass for 16') - the standard way an under-18 character is
  // smuggled into an adult premise.
  {
    id: 'underage-looks-underage',
    category: 'underage',
    severity: 'review',
    source: "\\b(?:looks?|looking|appears?|passes?\\s+for|could\\s+pass\\s+for|acts?\\s+like)\\s+(?:like\\s+)?(?:an?\\s+)?(?:1[0-7]|twelve|thirteen|fourteen|fifteen|sixteen|seventeen)\\b(?!\\s{0,2}(?:years?|yrs?)\\s+(?:younger|older))"
  },
  // Rule 2, ambiguous. Sub-high-school institution plus a neutral person
  // noun. Recall companion to the block-tier sibling.
  {
    id: 'underage-school-level-context',
    category: 'underage',
    severity: 'review',
    source: "\\b(?:(?:middle|elementary|grade|primary|grammar|nursery|pre)[\\s\\-]?school|jr\\.?[\\s\\-]?high(?:[\\s\\-]?school)?|junior[\\s\\-]?high(?:[\\s\\-]?school)?|kindergarten)(?:[\\s\\-]{0,2}(?:students?|kids?|child|children|pupils?)|ers?\\b)"
  },
  // Rule 2, gap #4. Non-US school systems name a sub-18 year group without
  // ever stating a number.
  {
    id: 'underage-school-system-intl',
    category: 'underage',
    severity: 'review',
    source: "\\byear\\s*(?:[7-9]|1[01])\\b|\\bsixth\\s+form\\b|\\bkey\\s+stage\\s+[1-4]\\b|\\blyc\\u00e9e\\b|\\bcoll\\u00e8ge\\b|\\bsecundaria\\b|\\b(?:real|haupt|mittel)schule\\b"
  },
  // Rule 2, ambiguous. The schoolgirl costume is one of the most common
  // entirely legal adult fantasies; it is only a violation when paired with
  // an actual minor age, so it is queued, never refused.
  {
    id: 'underage-schoolgirl-costume',
    category: 'underage',
    severity: 'review',
    source: "\\bschool[\\s\\-]?(?:girls?|boys?)\\b|\\bschool\\s?uniform\\b|\\bplaid\\s+skirt\\b"
  },
  // Rule 2, ambiguous. 'Teen' spans 13-19, and in adult-industry usage it
  // usually means an 18-19 performer, so it is legal here as often as not.
  {
    id: 'underage-teen-terms',
    category: 'underage',
    severity: 'review',
    source: "\\bteen(?:s|ager|agers|age|aged)?\\b"
  },
  // Rule 2, ambiguous. 'Tween' with no attached person noun is usually the
  // dialect contraction of 'between' in erotic prose, so it is queued rather
  // than refused.
  {
    id: 'underage-tween-bare',
    category: 'underage',
    severity: 'review',
    source: "(?<!['’])\\btweens?\\b(?!\\s+(?:her|his|their|my|the|us|them|those|these|our|your|ur|yer|em|each|both|a|an|any|every|all|one|two|its))"
  },
  // Rule 2, ambiguous. The words 'underage' and 'under 18' appear far more
  // often in good-faith disclaimers than in violations, so this is queued,
  // never refused.
  {
    id: 'underage-underage-word',
    category: 'underage',
    severity: 'review',
    source: "(?<!\\b(?:no|not|never|anyone|one|nobody|non|zero)\\s{1,2})\\b(?:under[\\s\\-]?aged?|under\\s+(?:the\\s+age\\s+of\\s+)?(?:18|eighteen)|age\\s+of\\s+consent)\\b"
  },
  // Rule 2, ambiguous. Prepubescent physical description used in place of an
  // explicit age.
  {
    id: 'underage-undeveloped-body',
    category: 'underage',
    severity: 'review',
    source: "\\b(?:child(?:[\\s\\-]?like|ish)|underdeveloped|undeveloped|barely\\s+developed|not\\s+(?:yet\\s+)?developed|budding)\\s{0,2}(?:body|figure|frame|chest|breasts|boobs|tits|hips|curves)\\b"
  },
  // Rule 4 explicitly governs images. Naming an image host is an offer to
  // move imagery off-platform even without a URL, which is what the rule
  // forbids, so it blocks on the bare brand name.
  {
    id: 'image-host-mention',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:imgur|redgifs|red\\s?gifs|gfycat|postimg|postimages|imgbb|imgbox|imgchest|imagebam|imagevenue|pixhost|photobucket|gyazo|lightshot|erome|imagefap|sendvid|imgtc|ibb\\.co|prnt\\.sc|catbox\\.moe|litterbox\\.catbox|flickr)\\b"
  },
  // Rule 4 / no external links. Markdown '[text](target)' and BBCode
  // [img]/[url] survive as plain text out of the rich-text editor and are
  // the standard way to smuggle a link past a naive URL scan.
  {
    id: 'markdown-or-bbcode-link',
    category: 'image-link',
    severity: 'block',
    source: "\\[[^\\]\\r\\n]{1,120}\\]\\(\\s*(?!(?:https?:\\/\\/)?(?:www\\.)?eropenpal\\.com\\b)(?:https?:\\/\\/)?(?:www\\.)?[a-z0-9][a-z0-9-]{0,62}\\.[a-z]{2,24}[^\\s)]{0,200}\\s*\\)|\\[(?:img|url|link)(?:=|\\])[^\\r\\n]{0,200}"
  },
  // Rule 4: requiring pictures in a response. The obligation verb alone is
  // not enough for block tier, because a scene can legitimately narrate 'you
  // must send a photo to your husband'.
  {
    id: 'must-send-pic',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:you\\s+)?(?:must|have\\s+to|need\\s+to|gotta|expected\\s+to)\\s+(?:send|share|provide|show)\\s+(?:(?:me|us)\\s+(?:a\\s+|an\\s+|your\\s+|ur\\s+|some\\s+)?(?:face\\s+|verification\\s+|timestamp(?:ed)?\\s+)?(?:pics?|pictures?|photos?|selfies?)\\b|(?:a\\s+|an\\s+|your\\s+|ur\\s+|some\\s+)?(?:face\\s+|verification\\s+|timestamp(?:ed)?\\s+)?(?:pics?|pictures?|photos?|selfies?)\\s+(?:first\\b|up\\s?front\\b|before\\s+(?:we|i|any|anything)\\b|to\\s+start\\b|in\\s+advance\\b|with\\s+(?:your|the)\\s+first\\b))"
  },
  // Rule 4: making a picture the price of a reply. The 'no X no Y'
  // construction is a formula with a fixed meaning, so it is precise enough
  // to hard block.
  {
    id: 'no-pic-no-chat',
    category: 'image-link',
    severity: 'block',
    source: "\\bno\\s+(?:face\\s+|nude\\s+|verification\\s+)?(?:pics?|pictures?|photos?|selfies?)\\b[\\s,.;:=\\/–—-]{0,8}\\bno\\s+(?:chat|reply|replies|response|respond|rp|roleplay|role\\s?play|talk|convo|conversation|deal|thanks|msg|message|messages|fun|play|contact\\b(?!\\s+(?:info|information|details|exchange)))\\b"
  },
  // Rule 4: pictures as a precondition for a reply, stated negatively ('no
  // reply without pics'). Semantically identical to 'no pic no chat'.
  {
    id: 'no-reply-without-pic',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:no|won'?t|wont|will\\s+not|do\\s+not|does\\s+not|do\\s?n'?t|dont)\\s+(?:\\w+\\s+){0,2}(?:reply|replies|respond|response|answer|chat|talk|rp|write|message|msg|dm|pm)\\w*(?:\\s+\\w+){0,2}\\s+(?:with)?out\\s+(?:a\\s+|an\\s+|any\\s+|your\\s+|ur\\s+)?(?:face\\s+|nude\\s+)?(?:pics?|pictures?|photos?|selfies?)\\b"
  },
  // Rule 4 / no external links. Generic catch for any domain (not just known
  // brands) written with a bracketed dot.
  {
    id: 'obfuscated-domain-bracketed-dot',
    category: 'image-link',
    severity: 'block',
    source: "\\b[a-z0-9][a-z0-9-]{1,62}\\s*(?:\\[\\s*(?:dot|\\.)\\s*\\]|\\(\\s*(?:dot|\\.)\\s*\\)|\\{\\s*(?:dot|\\.)\\s*\\})\\s*(?:com|net|org|io|co|me|gg|tv|cc|moe|xyz|info|biz|club|link|online|site|store|shop|app|dev|blog|media|pics|photos|gallery|video|tube|adult|porn|xxx|cams?|sexy|vip|icu|top|pw|su|ws|nu|onion|ly|gd|gl|to|sh|st|nz|ee|de|fr|nl|jp|br|uk|eu|us|ca|au|mx|ch|se|pl|cz|it|es|fi|dk|no|be|at|ie|in|ru|space|cloud|host|website|fans|art|life|live|fun|world|today|one|page|wiki)\\b"
  },
  // Rule 4 / no external links. Deliberate obfuscation of a known image
  // host, shortener or chat platform ('imgur dot com', 'reddit[.]com',
  // 'discord (dot) gg').
  {
    id: 'obfuscated-domain-known-host',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:imgur|redgifs|gfycat|catbox|postimg|postimages|imgbb|ibb|imgbox|imgchest|erome|pixhost|gyazo|discord|discordapp|reddit|kik|telegram|snapchat|whatsapp|wickr|onlyfans|fansly|pornhub|mega|dropbox|wetransfer|bit|tinyurl|linktr|linktree|twitter|instagram|tiktok|skype|t)\\s*(?:\\[\\s*(?:dot|\\.)\\s*\\]|\\(\\s*(?:dot|\\.)\\s*\\)|\\{\\s*(?:dot|\\.)\\s*\\}|<\\s*(?:dot|\\.)\\s*>|\\s+d[o0]t\\s+)\\s*(?:com|net|org|gg|me|io|ly|co|cc|tv|moe|sc|gl|to|nz|ee|xyz|info|onion)\\b"
  },
  // Rule 4 / no external links. Generic 'host dot tld' spoken-form
  // obfuscation for domains that are not on the known-brand list.
  {
    id: 'obfuscated-domain-spelled-dot',
    category: 'image-link',
    severity: 'block',
    source: "(?!(?:gmail|googlemail|yahoo|ymail|hotmail|outlook|protonmail|proton|icloud|me|aol|gmx|yandex|live|msn|mail|zoho|fastmail|tutanota)\\s)\\b[a-z0-9][a-z0-9-]{1,62}\\s+d[o0]t\\s+(?:com|net|org|io|gg|xyz|moe|info|biz|club|link|online|site|cc|tv|ru|de|nl|onion)\\b"
  },
  // Rule 4 / no external links. Bare domains with no scheme and no path
  // ('find the gallery on imgur.com').
  {
    id: 'offsite-bare-domain-hard-tld',
    category: 'image-link',
    severity: 'block',
    source: "(?<!@)(?!(?:gmail|googlemail|yahoo|ymail|hotmail|outlook|protonmail|proton|icloud|me|aol|gmx|yandex|live|msn|mail|zoho|fastmail|tutanota)\\.)\\b(?!eropenpal\\.com\\b)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.(?:com|org|biz|xyz|io|gg|cc|tv|moe|icu|onion|ru|jp|br)\\b"
  },
  // Rule 4 / no external links. Invite URLs are links, so they block
  // regardless of the unpublished contact-info rule - this is the link
  // itself, not a handle.
  {
    id: 'offsite-chat-invite-link',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:discord(?:app)?\\.(?:gg|com)\\/[^\\s]{2,}|discord\\.gg\\b|t\\.me\\/[^\\s]{2,}|wa\\.me\\/[^\\s]{2,}|chat\\.whatsapp\\.com\\b|kik\\.me\\/[^\\s]{2,}|telegram\\.me\\/[^\\s]{2,}|join\\.skype\\.com\\b)"
  },
  // Rule 4 / no external links. Catches schemeless URLs that carry a path -
  // 'imgur.com/a/x', 'reddit.com/r/dirtypenpals/...', 'catbox.moe/c/...'.
  {
    id: 'offsite-domain-with-path',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?!(?:mr|mrs|ms|dr|st|jr|sr|prof|capt|sgt|lt|vs|etc|fig|no|vol|approx|dept|est|inc|ltd|co)\\.)(?!(?:[a-z0-9-]+\\.)?eropenpal\\.com\\/)[a-z0-9][a-z0-9-]{0,61}(?:\\.[a-z0-9-]{1,61}){0,3}\\.(?:com|net|org|io|co|me|gg|tv|cc|moe|xyz|info|biz|club|link|online|site|store|shop|app|dev|blog|media|pics|photos|gallery|video|tube|adult|porn|xxx|cams?|sexy|vip|icu|top|pw|su|ws|nu|onion|ly|gd|gl|to|sh|st|nz|ee|de|fr|nl|jp|br|uk|eu|us|ca|au|mx|ch|se|pl|cz|it|es|fi|dk|no|be|at|ie|in|ru|space|cloud|host|website|fans|art|life|live|fun|world|today|one|page|wiki)\\/[^\\s]"
  },
  // Rule 4 forbids inappropriate/illegal image or link content, and the site
  // allows no external links in prompts. reddit.com is deliberately NOT
  // allowlisted.
  {
    id: 'offsite-url-scheme',
    category: 'image-link',
    severity: 'block',
    source: "\\bhttps?:\\/\\/(?!(?:[a-z0-9-]+\\.)?eropenpal\\.com\\b)[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)+"
  },
  // Rule 4 / no external links. Schemeless 'www.' addresses are the most
  // common way users paste a link out of a browser bar.
  {
    id: 'offsite-www-domain',
    category: 'image-link',
    severity: 'block',
    source: "\\bwww\\.(?!eropenpal\\.com\\b)[a-z0-9][a-z0-9-]*(?:\\.[a-z0-9-]+)+"
  },
  // Rule 4, final clause: 'Posts may not require pictures in a response.'
  // This is the most direct statement of that violation and the rule is
  // absolute, so it is a hard block.
  {
    id: 'pic-required-to-respond',
    category: 'image-link',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without)\\b(?:\\W+\\w+){0,2}\\W)\\b(?:face\\s+|body\\s+|full[\\s-]?body\\s+|verification\\s+|timestamp(?:ed)?\\s+|nude\\s+)?(?:pics?|pictures?|photos?|selfies?)(?:\\s+(?:is|are|r))?\\s+(?:absolutely\\s+)?(?:required|mandatory|compulsory|a\\s+must\\b|non[\\s-]?negotiable)"
  },
  // Rule 4's gender-verification clause in its sentence-level form. Requires
  // a proof verb, second-person address, a gender noun AND an image noun
  // within one sentence. That conjunction is what earns block tier.
  {
    id: 'prove-gender-with-pic',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:prove|verify|confirm)\\b[^.!?\\r\\n]{0,40}\\b(?:you'?re|you\\s+are|u\\s+r|ur|that\\s+you)\\b[^.!?\\r\\n]{0,30}\\b(?:girl|female|woman|guy|male|man|not\\s+a\\s+(?:bot|guy|dude|man))\\b[^.!?\\r\\n]{0,40}\\b(?:pics?|pictures?|photos?|selfies?|image)\\b|\\b(?:pics?|photos?|selfies?)\\b[^.!?\\r\\n]{0,40}\\b(?:to\\s+)?(?:prove|verify|confirm)\\b[^.!?\\r\\n]{0,30}\\b(?:you'?re|you\\s+are|ur|your)\\b[^.!?\\r\\n]{0,20}\\b(?:girl|female|woman|guy|male|man)\\b"
  },
  // Rule 4, second clause: 'Do not offer or request photos of private
  // individuals not involved in the exchange.' Asking the READER for imagery
  // of the reader's real wife/sister/ex is a direct request for a
  // non-participant's photos.
  {
    id: 'third-party-photo-request',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:send|share|post|show|swap|trade|dm|pm|text|want|need|looking\\s+for|got\\s+any)\\s+(?:me\\s+|us\\s+)?(?:some\\s+|a\\s+|any\\s+|the\\s+|your\\s+|ur\\s+)?(?:pics?|pictures?|photos?|images?|nudes?|selfies?|vids?|videos?)\\s+of\\s+(?:your|ur|yr)\\s+(?:hot\\s+|sexy\\s+|slutty\\s+|little\\s+|step[\\s-]?)?(?:wife|wifey|gf|girlfriend|husband|hubby|bf|boyfriend|sister|sis|brother|mom|mother|mum|aunt|cousin|niece|daughter|ex|ex[\\s-]?(?:gf|girlfriend|wife|bf)|friend|bestie|best\\s+friend|roommate|room[\\s-]?mate|neighbou?r|co[\\s-]?worker|coworker|classmate|teacher|student|boss|maid|nanny|babysitter)\\b"
  },
  // Rule 4 / no external links. Shorteners hide the destination (which may
  // be an image host or a commercial site), so they are blocked by name.
  {
    id: 'url-shortener',
    category: 'image-link',
    severity: 'block',
    source: "\\b(?:(?:bit|cutt|buff|adf|ow|rebrand|snip)\\.ly\\b|tinyurl\\.com\\b|goo\\.gl\\b|rb\\.gy\\b|shorturl\\.at\\b|shorte\\.st\\b|tiny\\.cc\\b|bl\\.ink\\b|clck\\.ru\\b|surl\\.li\\b|shrtco\\.de\\b|trib\\.al\\b|(?:t\\.co|is\\.gd|v\\.gd|s\\.id|u\\.to|x\\.co)\\/[^\\s]{1,64})"
  },
  // Rule 4 calls this out by name: 'Posts may not require pictures in a
  // response, INCLUDING FOR GENDER VERIFICATION.' Verification/proof imagery
  // is banned outright and blocks.
  {
    id: 'verification-or-proof-pic',
    category: 'image-link',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without)\\b(?:\\W+\\w+){0,2}\\W)(?:\\b(?:verification|verify|verifying|verified)\\s*[-–]?\\s*(?:pics?|pictures?|photos?|selfies?|shot|images?)\\b|\\b(?:pics?|pictures?|photos?|selfies?)\\s+(?:to|for)\\s+(?:verify|verification|prove|proof)\\b|\\b(?:gender|sex|age)\\s+verification\\b|\\bproof\\s*[-–]?\\s*(?:pics?|photos?|selfies?)\\b|\\b(?:pics?|photos?|selfies?)\\s+as\\s+proof\\b)"
  },
  // Catch-all for social/messenger @handles. Review per the
  // unpublished-contact-rule reasoning, and because an @handle alone is weak
  // evidence.
  {
    id: 'at-handle-mention',
    category: 'image-link',
    severity: 'review',
    source: "(?:^|[\\s(])@[a-z][a-z0-9._]{3,29}\\b"
  },
  // Off-platform contact handle, covering the legacy 'name#1234' tag and the
  // modern 'my discord' phrasings. Review per the unpublished-contact-rule
  // reasoning; discord.gg INVITE LINKS are a separate hard block.
  {
    id: 'discord-handle',
    category: 'image-link',
    severity: 'review',
    source: "\\b(?:my|on|via|through|add\\s+me\\s+on|dm\\s+me\\s+on|hit\\s+me\\s+up\\s+on|join\\s+my|join\\s+our)\\s+discord\\b|\\bdiscord\\s*(?:tag|id|handle|user(?:name)?|server|invite|@|:)|\\b[a-z0-9][a-z0-9_.]{1,30}#\\d{4}\\b"
  },
  // Off-platform contact detail; review tier per the
  // unpublished-contact-rule reasoning. Kept out of block deliberately
  // because the published rules do not forbid sharing your own email.
  {
    id: 'email-address',
    category: 'image-link',
    severity: 'review',
    source: "\\b[a-z0-9._%+-]{2,64}@[a-z0-9-]{2,63}(?:\\.[a-z0-9-]{2,63}){0,3}\\.[a-z]{2,24}\\b"
  },
  // Rule 4 governs offered/requested imagery; file-drop services are the
  // usual workaround once image hosts are blocked. Review because a few of
  // these words have innocent uses in fiction.
  {
    id: 'file-host-bare-mention',
    category: 'image-link',
    severity: 'review',
    source: "\\b(?:catbox|mega\\.nz|dropbox|wetransfer|we\\.tl|google\\s+drive|gdrive|pixeldrain|anonfiles|gofile|filebin)\\b"
  },
  // Off-platform contact handle. NOTE ON TIER: the site's 'no personal
  // contact information' rule is currently COMMENTED OUT and unpublished, so
  // blocking a handle would enforce a rule the user cannot read. Flip
  // severity if it is ever re-published.
  {
    id: 'kik-handle',
    category: 'image-link',
    severity: 'review',
    source: "\\bkik\\b"
  },
  // The explicit 'let's move this off-site' invitation - the behaviour the
  // commented-out contact rule targeted and the usual route to rule 4 image
  // trading. Review for the same unpublished-rule reason.
  {
    id: 'move-off-platform-invite',
    category: 'image-link',
    severity: 'review',
    source: "\\b(?:hit\\s+me\\s+up|add\\s+me|dm\\s+me|msg\\s+me|message\\s+me|find\\s+me|reach\\s+me|contact\\s+me|catch\\s+me|talk\\s+to\\s+me|chat\\s+with\\s+me)\\s+(?:on|at|via|through|thru)\\s+(?:kik|discord|telegram|snap(?:chat)?|whats\\s?app|wickr|session|signal|insta(?:gram)?|twitter|reddit|skype|line|viber|email|e-?mail|text|sms|gmail|matrix|threema|element|tox)\\b|\\bmy\\s+(?:kik|discord|telegram|snap(?:chat)?|insta(?:gram)?|skype|wickr|email|e-?mail)\\s*(?:is\\b|:|=|@)"
  },
  // Same rule and tier as the plain email pattern, catching the 'name at
  // gmail dot com' spoken form that the address regex misses.
  {
    id: 'obfuscated-email',
    category: 'image-link',
    severity: 'review',
    source: "\\b[a-z0-9._-]{2,32}\\s*(?:\\(|\\[)?\\s*(?:at|@)\\s*(?:\\)|\\])?\\s*(?:gmail|yahoo|hotmail|outlook|protonmail|proton|icloud|aol|gmx|yandex|mail)\\s*(?:\\(|\\[)?\\s*(?:dot|\\.)\\s*(?:\\)|\\])?\\s*(?:com|net|org|me|ru|co)\\b"
  },
  // Rule 4 / no external links, recall-over-precision tier. These TLDs are
  // real but each is also an ordinary word, an RP abbreviation ('AU' =
  // alternate universe) or a foreign-language word.
  {
    id: 'offsite-bare-domain-wordlike-tld',
    category: 'image-link',
    severity: 'review',
    source: "(?<!@)(?!(?:gmail|googlemail|yahoo|ymail|hotmail|outlook|protonmail|proton|icloud|me|aol|gmx|yandex|live|msn|mail|zoho|fastmail|tutanota)\\.)\\b[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\\.(?:net|info|site|website|store|shop|link|click|space|cloud|host|vip|club|online|media|pics|photos|gallery|blog|wiki|tube|adult|porn|xxx|cams?|sexy|dev|pw|su|ws|nu|au|mx|ch|se|pl|cz|de|fr|nl|eu|uk)\\b"
  },
  // Off-platform contact detail; review tier per the
  // unpublished-contact-rule reasoning. Phone numbers also carry real-world
  // doxxing risk worth a human look.
  {
    id: 'phone-number',
    category: 'image-link',
    severity: 'review',
    source: "(?<![\\w.-])(?:\\+?\\d{1,3}[ .-]?)?\\(?\\d{3}\\)?[ .-]\\d{3}[ .-]\\d{4}(?![\\w-])"
  },
  // Rule 4 bans REQUIRING pictures in a response; it does not ban two
  // consenting adults trading their own images. 'Pics first' sits on that
  // line, so it goes to the human queue.
  {
    id: 'pic-exchange-precondition',
    category: 'image-link',
    severity: 'review',
    source: "\\b(?:pics?|pictures?|photos?|selfies?)\\s+(?:first\\b|up\\s?front\\b|before\\s+(?:we|i|any|anything|chat|rp)\\b)|\\b(?:trade|swap|exchange)\\s+(?:some\\s+|a\\s+few\\s+)?(?:pics?|pictures?|photos?|selfies?|nudes?)\\b|\\b(?:pic|photo|selfie)[\\s-]?(?:trade|swap|exchange)\\b"
  },
  // Rule 4's gender-verification clause in its bare form ('Prove you're a
  // girl.'), where no image noun is stated.
  {
    id: 'prove-your-gender-standalone',
    category: 'image-link',
    severity: 'review',
    source: "\\bprove\\s+(?:to\\s+me\\s+)?(?:that\\s+)?(?:you'?re|you\\s+are|u\\s+r|ur)\\s+(?:a\\s+)?(?:girl|gal|female|woman|guy|male|man|real|not\\s+a\\s+(?:bot|guy|dude|man))\\b(?=\\s*[.,!?;:)\\r\\n]|\\s*$|\\s+(?:first|before|by|with|via|w\\/))"
  },
  // Off-platform contact handles on encrypted messengers; review per the
  // unpublished-contact-rule reasoning. Worth queueing because moving to an
  // encrypted channel precedes the image trading rule 4 does prohibit.
  {
    id: 'secure-messenger-handle',
    category: 'image-link',
    severity: 'review',
    source: "\\bwickr\\b|\\bthreema\\b|\\bsession\\s+id\\b|\\bwhats\\s?app\\b|\\bsignal\\s+(?:me\\b|app\\b|id\\b|user(?:name)?\\b)|\\bmatrix\\s+id\\b|\\bviber\\b|\\bskype\\b|\\bkakao(?:talk)?\\b|\\bwechat\\b|\\bline\\s+id\\b"
  },
  // Off-platform contact handle; review tier per the
  // unpublished-contact-rule reasoning. Commercial 'premium snap'
  // advertising is handled separately at block tier because rule 3 IS
  // published.
  {
    id: 'snapchat-handle',
    category: 'image-link',
    severity: 'review',
    source: "\\bsnap\\s?chat\\b|\\bsnap\\s*(?:name|id|handle|user(?:name)?)\\b|\\bsnap\\s*[:@]\\s*[a-z0-9._-]{3,20}\\b|\\b(?:my|ur|your)\\s+snap\\s+is\\b|\\badd\\s+(?:me\\s+)?on\\s+snap\\b"
  },
  // Rule 4 / no external links: 'r/subreddit' and 'u/username' are off-site
  // pointers and reddit is explicitly NOT allowlisted. Review because a bare
  // handle is not itself a URL.
  {
    id: 'subreddit-or-reddit-user-handle',
    category: 'image-link',
    severity: 'review',
    source: "(?:^|[\\s(\\[>\"'])\\/?(?:r|u)\\/[a-z0-9][a-z0-9_-]{2,21}\\b"
  },
  // Off-platform contact handle; review tier per the
  // unpublished-contact-rule reasoning. t.me links are hard-blocked
  // separately as links under rule 4.
  {
    id: 'telegram-handle',
    category: 'image-link',
    severity: 'review',
    source: "\\btelegram\\s*[@:]|\\btelegram\\s+(?:handle|user(?:name)?|id|group|channel|me)\\b|\\b(?:my|on|via|through|add\\s+me\\s+on|hit\\s+me\\s+up\\s+on)\\s+telegram\\b"
  },
  // Rule 4's non-participant clause where the referent is a pronoun ('share
  // pics of her'). The pronoun may refer to a fictional character or a real
  // third party, so it is queued.
  {
    id: 'third-party-photo-generic',
    category: 'image-link',
    severity: 'review',
    source: "\\b(?:send|share|show|post|dm|pm)\\s+(?:me\\s+)?(?:some\\s+|a\\s+|the\\s+|any\\s+)?(?:pics?|pictures?|photos?|nudes?|images?)\\s+of\\s+(?:her|him|them|his|hers|their)\\b"
  },
  // Rule 3 bans advertising adult websites, adult stores and similar
  // commercial enterprises.
  {
    id: 'money-adult-site-advert',
    category: 'money',
    severity: 'block',
    source: "\\b(?:check\\s+out|visit|join|sign\\s+up\\s+(?:for|at)|come\\s+to)\\s+(?:my\\s+|our\\s+|this\\s+)?(?:adult\\s+(?:site|store|website|shop)|cam\\s+site|sex\\s+shop|porn\\s+site)\\b|\\b(?:my|our)\\s+(?:adult\\s+)?(?:site|website|store|shop)\\s+(?:is\\s+(?:live|up|at)|link)\\b"
  },
  // Rule 3 names cam shows and chat services explicitly as prohibited
  // advertising.
  {
    id: 'money-cam-show-offer',
    category: 'money',
    severity: 'block',
    source: "\\b(?:i|we)\\s+(?:do|host|run|offer|stream)\\s+(?:live\\s+)?cam\\s?(?:shows?|sessions?)\\b|\\bmy\\s+cam\\s?(?:show|room|site|link|page)\\s+(?:is\\s+(?:live|open|on|up|at)\\b|link\\b|opens?\\b|starts?\\b|tonight\\b|now\\b)|\\bcam\\s?shows?\\s+(?:available|start(?:ing)?\\s+at)\\b|\\bcatch\\s+me\\s+(?:live\\s+)?on\\s+cam\\b|\\bprivate\\s+cam\\s+shows?\\b"
  },
  // Rule 3 bans advertising adult websites, chat services and cam shows.
  // Every token here is a named commercial monetisation platform; naming one
  // in a penpal prompt is advertising it.
  {
    id: 'money-creator-platform-named',
    category: 'money',
    severity: 'block',
    source: "\\b(?:fansly|patreon|many\\s?vids|chaturbate|fan\\s?centro|loyal\\s?fans|myfreecams|my\\s?free\\s?cams|stripchat|bongacams|cam4|clips4sale|just\\s?for\\s?fans|fanvue|nite\\s?flirt|sextpanther|sext\\s?panther|avnstars|iwantclips|fanhouse|modelhub|camsoda|livejasmin|streamate|flirt4free)\\b"
  },
  // Rule 3. A literal wallet address pasted into a prompt has exactly one
  // purpose: receiving real money.
  {
    id: 'money-crypto-address',
    category: 'money',
    severity: 'block',
    source: "\\b(?:bc1[a-z0-9]{25,50}|0x[a-f0-9]{40}|[13](?=[a-km-z1-9]{25,34}\\b)(?=[a-km-z1-9]{0,33}[1-9])[a-km-z1-9]{25,34})\\b"
  },
  // Rule 3. Requesting crypto is soliciting compensation, and crypto is the
  // most common rail once the obvious ones are filtered.
  {
    id: 'money-crypto-solicit',
    category: 'money',
    severity: 'block',
    source: "\\b(?:btc|bitcoin|eth|ethereum|usdt|ltc|litecoin|monero|xmr|dogecoin|crypto)\\s+(?:wallet|address|addy|donations?|payments?|tips?|only)\\b|\\b(?:send|pay|tip)\\s+(?:me\\s+)?(?:in\\s+|with\\s+|some\\s+)?(?:btc|bitcoin|ethereum|usdt|monero|xmr)\\b"
  },
  // Rule 3. Offering a price list on request solicits compensation for
  // interactions the site requires to be free.
  {
    id: 'money-dm-for-rates',
    category: 'money',
    severity: 'block',
    source: "\\b(?:dm|pm|msg|message|inbox|hmu)\\s+(?:me\\s+)?(?:for|4)\\s+(?:my\\s+)?(?:rates?|pricing|prices?|price\\s+list|menu)\\b"
  },
  // Rule 3 names donations verbatim as prohibited.
  {
    id: 'money-donation-solicit',
    category: 'money',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without|against|hate)\\b(?:\\W+\\w+){0,2}\\W)(?:\\bdonat(?:e|ions?)\\s+(?:to\\s+)?(?:me|my|here|link)\\b|\\bdonations?\\s+(?:appreciated|welcome|accepted|open|link)\\b|\\baccepting\\s+donations?\\b)"
  },
  // Rule 3 bans advertising commercial enterprises. Incall/outcall and
  // escort-service/rate language are trade-specific real-world sex-work
  // advertising vocabulary.
  {
    id: 'money-escort-commerce',
    category: 'money',
    severity: 'block',
    source: "\\b(?:in-?call|out-?call)s?\\b|\\bescort\\s+(?:service|services|agency|ads?|available|rates?)\\b|\\bfull\\s+service\\s+(?:rates?|available)\\b|\\bgfe\\s+(?:rates?|available|only)\\b"
  },
  // Rule 3 names gifts verbatim as prohibited compensation, and gift cards
  // are the standard workaround once payment rails are filtered.
  {
    id: 'money-giftcard-solicit',
    category: 'money',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without|against|hate)\\b(?:\\W+\\w+){0,2}\\W)(?:\\b(?:send|sending|buy|buying|get|getting|want|need|accept(?:ing)?)\\s+(?:me|us)\\s+(?:an?\\s+|some\\s+)?(?:amazon\\s+|steam\\s+|itunes\\s+|google\\s+play\\s+|visa\\s+)?(?:e-?)?gift\\s?cards?\\b|\\baccept(?:ing)?\\s+(?:e-?)?gift\\s?cards?\\b|\\bgift\\s?cards?\\s+(?:only|appreciated|accepted|please|required)\\b)"
  },
  // Rule 3 / rule 4. 'Link in bio' is the universal off-platform funnel to
  // paid content and exists for no other purpose in a prompt.
  {
    id: 'money-link-in-bio',
    category: 'money',
    severity: 'block',
    source: "\\b(?:links?\\s+(?:in|on)\\s+(?:my\\s+)?bio\\b|bio\\s+link\\b|links?\\s+in\\s+(?:my\\s+)?profile\\b)"
  },
  // Rule 3. Handles the 'my OF' / '0F' abbreviation for OnlyFans, the most
  // common way users dodge a naive keyword filter.
  {
    id: 'money-of-abbreviation',
    category: 'money',
    severity: 'block',
    source: "\\b(?:(?:my|her|his|our)\\s+(?:0f|of)\\s+(?:page|link|acc(?:oun)?t|profile|content|subs?)\\b|(?:sub(?:scribe)?|subbing|join|check\\s+out|link\\s+to)\\s+(?:to\\s+)?my\\s+(?:0f|of)\\b)"
  },
  // Rule 3 bans advertising adult commercial services. Covers onlyfans plus
  // the standard obfuscations: 0nlyfans, only.fans, only-fans, only_fans,
  // onlyf4ns, onlyfanz.
  {
    id: 'money-onlyfans-obfuscated',
    category: 'money',
    severity: 'block',
    source: "\\b(?:[o0]nly[._\\-]?f[a4]n[sz]|0nly\\s+f[a4]n[sz]|only\\s+f4ns|onlyfanz)\\b"
  },
  // Rule 3. Catches the space-separated spelling of the platform, but only
  // when a possessive or a page/subscribe marker proves it is the brand
  // rather than the English words 'only fans'.
  {
    id: 'money-onlyfans-spaced-context',
    category: 'money',
    severity: 'block',
    source: "\\b(?:(?:my|our|her|his)\\s+only\\s+fans\\b(?!\\s+(?:are|were|have|had|include|were)\\b)|only\\s+fans\\s+(?:link|page|acc(?:oun)?t|profile|sub(?:scription)?s?)\\b|(?:sub(?:scribe)?|join|check\\s+out)\\s+(?:to\\s+)?(?:my\\s+)?only\\s+fans\\b)"
  },
  // Rule 3. 'Pay me' bound to a concrete real currency amount is
  // solicitation, not fantasy.
  {
    id: 'money-pay-me-amount',
    category: 'money',
    severity: 'block',
    source: "\\bpay(?:ing)?\\s+(?:me|up)\\b[^\\n]{0,15}\\$\\s?\\d|\\bpay\\s+me\\s+\\d{1,5}\\s*(?:usd|dollars|bucks|eur|gbp|cad|aud|quid)\\b|\\$\\s?\\d{1,5}\\s*(?:to|before)\\s+(?:start|begin|chat|talk|play|continue)\\b"
  },
  // Rule 3 (with rule 4 on links). A literal payment or creator-monetisation
  // domain is advertising for a commercial enterprise.
  {
    id: 'money-payment-domain',
    category: 'money',
    severity: 'block',
    source: "\\b(?:paypal\\.me|cash\\.app|venmo\\.com|ko-?fi\\.com|buymeacoffee\\.com|throne\\.(?:me|com)|patreon\\.com|onlyfans\\.com|fansly\\.com|linktr\\.ee|allmylinks\\.com|beacons\\.ai|manyvids\\.com|chaturbate\\.com|fancentro\\.com|loyalfans\\.com|justfor\\.fans)\\b"
  },
  // Rule 3 requires all interactions on the site to be free. Gating the
  // roleplay itself behind a payment is exactly the prohibited behaviour.
  {
    id: 'money-payment-required',
    category: 'money',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without|against|hate)\\b(?:\\W+\\w+){0,2}\\W)(?:\\b(?:payment|deposit)\\s+(?:is\\s+)?(?:required|up\\s?front|upfront|in\\s+advance|first)\\b|\\bno\\s+(?:pay|payment|money|cash)\\s*,?\\s*no\\s+(?:play|chat|rp|talk|reply)\\b|\\bpaid\\s+(?:sessions?|rp|roleplay|chats?|dms?|content)\\b)"
  },
  // Rule 3. 'Premium Snapchat' is a paid adult subscription product - a
  // commercial chat service being advertised. This is the deliberate
  // carve-out from the review-tier contact-handle group.
  {
    id: 'money-premium-snap',
    category: 'money',
    severity: 'block',
    source: "\\b(?:premium|prem|paid|vip|exclusive)\\s+snap(?:chat)?\\b|\\bsnap(?:chat)?\\s+premium\\b"
  },
  // Rule 3 bans advertising commercial enterprises; promo codes, affiliate
  // links and percentage discounts are pure commercial advertising with no
  // roleplay function.
  {
    id: 'money-promo-affiliate',
    category: 'money',
    severity: 'block',
    source: "\\b(?:promo|discount|coupon|referral)\\s+codes?\\b|\\buse\\s+(?:my\\s+)?(?:promo\\s+|discount\\s+|coupon\\s+|referral\\s+)?code\\s+(?!word\\b|words\\b|name\\b|names\\b|phrase\\b|red\\b|green\\b|blue\\b|yellow\\b)[a-z0-9]{0,11}\\d[a-z0-9]{0,11}\\b|\\b(?:affiliate|referral)\\s+links?\\b|\\b\\d{1,2}%\\s*off\\b"
  },
  // Rule 3 forbids offering or soliciting money. A named payment rail
  // followed by an identity/transfer marker ("my cashapp is $x", "Venmo:
  // @x") is an unambiguous real-money handoff with no in-fiction reading.
  {
    id: 'money-rail-handle-context',
    category: 'money',
    severity: 'block',
    source: "\\b(?:c[a@]sh\\s?[a@]pp|v[e3]nmo|p[a@]y\\s?p[a@]l|z[e3]ll[e3]|revolut|apple\\s?pay|google\\s?pay|interac)\\b(?:\\s*[:=]|\\s*(?:handle|tag|user(?:name)?|acct|account|info|details|link)\\b|\\s*[@$][a-z0-9._-]{2,}|\\s*(?:is|are)\\s*(?:[@$][a-z0-9._-]{2,}|on\\s+request\\b|in\\s+(?:my\\s+)?(?:bio|profile)\\b|below\\b|above\\b))"
  },
  // Rule 3. "venmo me" / "cashapp me" uses the rail name as a verb aimed at
  // the author - a direct instruction to move real money, and rule 3 states
  // that all interactions here are free.
  {
    id: 'money-rail-imperative',
    category: 'money',
    severity: 'block',
    source: "\\b(?:c[a@]sh\\s?[a@]pp|v[e3]nmo|p[a@]y\\s?p[a@]l|z[e3]ll[e3])\\s+(?:me|us)\\b"
  },
  // Rule 3. A transfer verb plus 'via <rail>' is an explicit instruction for
  // a real payment.
  {
    id: 'money-rail-send-via',
    category: 'money',
    severity: 'block',
    source: "\\b(?:send|pay|transfer|deposit|tip)(?:s|ed|ing)?\\s+(?:me\\s+|it\\s+|them\\s+)?(?:\\$?\\d+\\s+)?(?:via|thru|through|over)\\s+(?:my\\s+)?(?:c[a@]sh\\s?[a@]pp|v[e3]nmo|p[a@]y\\s?p[a@]l|z[e3]ll[e3]|revolut|crypto|bitcoin|btc|eth)\\b"
  },
  // Rule 3. A quoted rate card is unambiguous commerce, whatever currency it
  // is denominated in.
  {
    id: 'money-rates-quoted',
    category: 'money',
    severity: 'block',
    source: "\\brates?\\s+(?:start(?:ing)?\\s+(?:at|from)|are\\s+as\\s+follows|on\\s+request|available\\s+on\\s+request|in\\s+(?:my\\s+)?dms?)\\b|\\bprice\\s+list\\b(?=\\s{0,3}(?:in|on|is|are|available|below|starts?|dm|pm|via)\\b)|\\b(?:my|our)\\s+price\\s+list\\b|\\bpricing\\s+(?:list|menu|below)\\b"
  },
  // Rule 3. Selling nudes, clips, customs or worn items is the clearest form
  // of soliciting compensation; rule 4 separately restricts image trading.
  {
    id: 'money-selling-content',
    category: 'money',
    severity: 'block',
    source: "(?<!\\b(?:no|not|never|non|anti|zero|dont|don['’]?t|doesn['’]?t|won['’]?t|wont|isn['’]?t|aren['’]?t|nothing|nobody|none|without|against|hate)\\b(?:\\W+\\w+){0,3}\\W)(?:\\bsell(?:ing|s)?\\s+(?:my\\s+|her\\s+|his\\s+)?(?:nudes?|lewds?|feet\\s+pics?|panties|worn\\s+(?:panties|underwear|socks)|customs?|content|clips?)\\b|\\b(?:selling\\s+my|i\\s+sell|for\\s+sale)\\b[^\\n]{0,20}\\b(?:nudes?|lewds?|pics?|photos?|vids?|videos?|clips?|content|panties)\\b|\\b(?:nudes?|lewds?|pics?|photos?|vids?|videos?|clips?|customs?|panties|worn\\s+(?:panties|underwear|socks))\\s+(?:are\\s+)?for\\s+sale\\b)"
  },
  // Rule 3. Directing readers to subscribe to the author's own page/feed
  // solicits compensation and advertises a commercial enterprise.
  {
    id: 'money-subscribe-to-my',
    category: 'money',
    severity: 'block',
    source: "\\b(?:sub(?:scribe|scription)?|subbing|join)\\s+(?:to\\s+)?my\\s+(?:page|profile|channel|feed|content|site|fan\\s?club|exclusive)\\b"
  },
  // Rule 3. Per-unit pricing of pictures, videos or messages is a commercial
  // transaction with no plausible in-story reading.
  {
    id: 'money-unit-pricing',
    category: 'money',
    severity: 'block',
    source: "\\$\\s?\\d{1,5}(?:\\.\\d{2})?\\s*(?:\\/|per\\s+|a\\s+|each\\s+for\\s+)\\s*(?:pic|photo|nude|vid|video|clip|min(?:ute)?|message|msg|text|sext|reply|scene|custom|set)s?\\b|\\b(?:pics?|photos?|nudes?|vids?|videos?|clips?|customs?|sets?)\\s+(?:are|start(?:ing)?\\s+at|cost)\\s*\\$\\s?\\d"
  },
  // Rule 3. An Amazon wishlist or Throne is a request for compensation in
  // goods.
  {
    id: 'money-wishlist-registry',
    category: 'money',
    severity: 'block',
    source: "\\b(?:amazon|amzn|throne)\\s+(?:wish\\s?list|registry)\\b|\\bwish\\s?list\\s+(?:link|below|in\\s+bio|on\\s+my\\s+profile)\\b|\\b(?:send|buy|get)\\s+(?:me\\s+)?(?:something\\s+)?(?:from|off)\\s+my\\s+wish\\s?list\\b"
  },
  // Rule 3. Booking and slot language is how paid sessions get advertised -
  // but it is also entirely plausible inside therapist/patient, massage or
  // high-class-escort roleplay, so it is queued.
  {
    id: 'money-booking-language',
    category: 'money',
    severity: 'review',
    source: "\\b(?:book\\s+(?:a\\s+|your\\s+|my\\s+)?(?:session|slot|appointment|time)|book\\s+me\\b|by\\s+appointment\\s+only|slots?\\s+available|taking\\s+bookings?|now\\s+booking)\\b"
  },
  // Rule 3 names 'any other compensation'. Real hardship asks and in-fiction
  // dependency dynamics look identical in plain text, so a human reviews
  // rather than the engine refusing.
  {
    id: 'money-financial-support-ask',
    category: 'money',
    severity: 'review',
    source: "\\b(?:compensat(?:e|ed|ion)\\s+(?:me\\b|expected|required|for\\s+my\\s+time)|(?:expect|want|need|require|seeking)\\w*\\s+compensation|reimburse(?:ment|d)?\\s+(?:me|for\\s+my)|financial\\s+(?:help|support|assistance|aid)|help\\s+(?:me\\s+)?(?:with|pay)\\s+(?:my\\s+)?(?:rent|bills|tuition|groceries))\\b"
  },
  // Rule 3 in direct tension with the product: financial domination is a
  // legitimate kink to roleplay here and only becomes a violation when real
  // money actually moves. Queued, not blocked - do not hard-block a kink.
  {
    id: 'money-findom-kink-vocab',
    category: 'money',
    severity: 'review',
    source: "\\b(?:fin\\s?dom(?:me|ination)?|financial\\s+dom(?:ination|me|inatrix)?|pay\\s?pig|cash\\s?pig|wallet\\s+(?:drain|slave|rape)|human\\s+atm|money\\s+slave|cash\\s?(?:master|slave)|drain(?:ing|ed)?\\s+(?:my|your)\\s+(?:wallet|bank|account))\\b"
  },
  // Rule 3. 'Generous gentlemen only' and 'serious inquiries' are the
  // established euphemisms for a paid arrangement, but they are coded rather
  // than explicit, so a human decides.
  {
    id: 'money-generous-solicitation-code',
    category: 'money',
    severity: 'review',
    source: "\\b(?:generous\\s+(?:men|man|guys?|gentlemen|daddy|daddies|types?|only)|must\\s+be\\s+generous|serious\\s+(?:buyers?|inquiries)|paying\\s+(?:customers?|clients?|subs?)|well[\\s-]?off\\s+(?:men|gentlemen))\\b"
  },
  // Rule 3 names gifts as prohibited compensation, but 'buy me something
  // nice' is also an extremely common in-character line in sugar and brat
  // roleplay.
  {
    id: 'money-gifts-and-treats',
    category: 'money',
    severity: 'review',
    source: "\\b(?:amazon\\s+gift|send\\s+(?:me\\s+)?gifts?|buy\\s+me\\s+(?:something|stuff|things|nice)|treat\\s+me\\s+to|spoil\\s+me\\s+with)\\b"
  },
  // Rule 3. An hourly or nightly price is standard escort-ad structure - but
  // the identical string is equally standard in escort ROLEPLAY, which is
  // legitimate here.
  {
    id: 'money-hourly-rate',
    category: 'money',
    severity: 'review',
    source: "\\$\\s?\\d{2,6}(?:[.,]\\d{3})?\\s*(?:\\/|an?\\s+|per\\s+)\\s*(?:h(?:ou)?rs?|hours?|nights?|days?|weeks?|months?|sessions?|dates?)\\b"
  },
  // Rule 3. A real payment rail named anywhere in a prompt deserves a human
  // look even without an attached handle - but on its own it can genuinely
  // be fiction, so it is queued rather than blocked.
  {
    id: 'money-rail-bare-mention',
    category: 'money',
    severity: 'review',
    source: "\\b(?:c[a@]sh\\s?[a@]pp|v[e3]nmo|p[a@]y\\s?p[a@]l|z[e3]ll[e3]|revolut|apple\\s?pay|google\\s?pay|western\\s+union|money\\s?gram)\\b"
  },
  // Rule 3. Sugar-daddy dynamics and 'spoil me' are mainstream roleplay
  // fantasies here, but they are also the standard cover language for real
  // solicitation. Queue, never block.
  {
    id: 'money-sugar-arrangement',
    category: 'money',
    severity: 'review',
    source: "\\b(?:sugar\\s+(?:daddy|dad|momma|mommy|mama|baby|babe)|sugaring|allowance|spoil\\s+me|spoil\\s+you|kept\\s+(?:woman|boy|girl))\\b"
  },
  // Rule 3. Tipping language solicits compensation, but 'tips' also means
  // advice and 'tip' is anatomical in erotica, so this is queued rather than
  // blocked.
  {
    id: 'money-tips-appreciated',
    category: 'money',
    severity: 'review',
    source: "\\btips?\\s+(?:are\\s+)?(?:appreciated|welcome|welcomed|accepted|encouraged)\\b|\\btip\\s+jar\\b|\\btip\\s+me\\b(?!\\s+(?:over|into|off|forward|back|out))"
  },
  // Rule 3. 'Tribute' is findom vocabulary that may or may not involve real
  // money, so it is queued for a human rather than blocked.
  {
    id: 'money-tribute-transaction',
    category: 'money',
    severity: 'review',
    source: "\\b(?:pay|send|owe|offer|give|leave)(?:ing|s|ed)?\\s+(?:me\\s+)?(?:a\\s+|your\\s+)?tributes?\\b|\\btributes?\\s+(?:me|first|required|expected|only|before)\\b|\\btribute\\s+(?:amount|money|cash)\\b"
  },
  // Rule 1. Genocidal or eliminationist advocacy against a protected group.
  // No plausible consensual-scene reading; hard refuse.
  {
    id: 'hate-genocidal-advocacy',
    category: 'harassment',
    severity: 'block',
    source: "\\b(?:gas\\s+(?:the\\s+)?(?:jews|k[iy1!]k[e3]s)|kill\\s+all\\s+(?:the\\s+)?(?:jews|muslims|blacks|gays|f[a4]gg?[o0]ts|trann(?:ies|ys)|n[i1!|]gg?[e3]rs|whites|asians|mexicans|immigrants|trans\\s+(?:people|women|men))|death\\s+to\\s+(?:all\\s+)?(?:jews|muslims|gays|blacks|trans|f[a4]gg?[o0]ts)|hitler\\s+(?:was\\s+right|did\\s+nothing\\s+wrong)|(?:six|6)\\s*million\\s+(?:wasn'?t|was\\s+not)\\s+enough)"
  },
  // Rule 1. Fixed xenophobic dismissal aimed at a reader. Not scene
  // vocabulary.
  {
    id: 'hate-goback-dehumanizing',
    category: 'harassment',
    severity: 'block',
    source: "\\bgo\\s+back\\s+(?:to\\s+(?:your\\s+(?:own\\s+)?(?:country|shithole)|the\\s+jungle)|(?:to\\s+)?where\\s+(?:you|they|he|she)\\s+came\\s+from)\\b"
  },
  // Rule 1, extended by policy to self-harm incitement aimed at a reader.
  // Hard refuse - no penpal ad needs to tell a reader to kill themselves.
  {
    id: 'selfharm-incitement',
    category: 'harassment',
    severity: 'block',
    source: "(?<!\\b(?:don'?t|do\\s+not|didn'?t|won'?t|wouldn'?t|never|almost|nearly|might|may|could|would|gonna|going\\s+to|want(?:ed)?\\s+to|wanna|thinking\\s+about|i'?d|we'?d|let\\s+me|let\\s+him|let\\s+her|help(?:ing)?\\s+(?:you|me)\\s+not)\\s)\\b(?:k\\.?y\\.?s|kill\\s+your\\s?self|kill\\s+ur\\s?self|go\\s+(?:and\\s+)?die\\b(?!\\s+(?:happy|content|smiling|satisfied|laughing))|neck\\s+your\\s?self|hang\\s+your\\s?self\\b(?!\\s+(?:from|upside|by\\s+(?:the|your)|in\\s+(?:the\\s+)?(?:harness|rope|ropes|suspension|sling)))|(?<!\\b(?:get|gets|getting|jerk|jerks|jerking|rub|rubs|rubbing|finish|finishes|finishing|wank|wanks|wanking|toss|tosses|tossing|beat|beats|beating|one|it)\\s(?:you|him|her|them|me|us)?\\s?)off\\s+your\\s?self|drink\\s+bleach|slit\\s+your\\s+wrists?|the\\s+world\\s+would\\s+be\\s+better\\s+without\\s+you)\\b"
  },
  // Rule 1. Same hard-slur tier as slur-ethnic-unambiguous, but these four
  // have rare innocent homographs, so each carries an explicit carve-out for
  // its known benign use.
  {
    id: 'slur-ethnic-guarded',
    category: 'harassment',
    severity: 'block',
    source: "\\bchinks?\\b(?!\\s+(?:in|of|between|where|through|behind|under|underneath|beneath|around|near|at|let|widened|appeared))|\\bspics?\\b(?!\\s+and\\s+span\\b)|\\bgooks?\\b|\\b(?<!maine\\s)(?<!\\bthe\\s)coons?\\b(?!\\s?(?:hound|skin|dog))"
  },
  // Rule 1 (be respectful). Ethnic/religious slurs with no non-slur English
  // sense, no reclaimed in-community sense, and no role in consensual
  // race-play scene vocabulary.
  {
    id: 'slur-ethnic-unambiguous',
    category: 'harassment',
    severity: 'block',
    source: "\\b(?:k[iy1!]k[e3]s?|wetbacks?|wet-backs?|beaners?|spear[\\s\\-]?chuckers?|porch[\\s\\-]?monkeys?|jungle[\\s\\-]?bunn(?:y|ies)|zipper[\\-]?heads?|(?:towel|rag)[\\s\\-]?heads?\\b(?!\\s+to\\s+toe)|pickaninn(?:y|ies)|golliwogs?|muzzies?|christ[\\s\\-]?killers?|sand[\\s\\-]?n[i1!|]gg?[e3a]rs?)\\b"
  },
  // Rule 1. A slur used in the plural to exclude a whole class of members,
  // or in a violence frame, is real-world targeting of other users rather
  // than scene text.
  {
    id: 'slur-group-exclusion-or-violence',
    category: 'harassment',
    severity: 'block',
    source: "\\b(?:no|i\\s+hate|we\\s+hate|kill|gas|lynch|hang|beat)\\s+(?:the\\s+|these\\s+|those\\s+|all\\s+|dirty\\s+|filthy\\s+|fucking\\s+){0,2}(?:n[i1!|]gg?[e3]rs|n[i1!|]gg?as|f[a4]gg?[o0]ts|trann(?:ies|ys)|k[iy1!]k[e3]s|chinks|spics|towel\\s?heads|rag\\s?heads)\\b(?!\\s+like\\s+(?:me|us)\\b)"
  },
  // Rule 1's 'calling out' clause. Naming another member's handle inside a
  // negative claim, or telling readers to avoid a named handle. Review
  // because the accusation may well be true and an admin should see it
  // either way.
  {
    id: 'callout-handle-negative',
    category: 'harassment',
    severity: 'review',
    source: "(?:(?<![a-z0-9._%+\\-])@[a-z0-9_.\\-]{2,30}|\\b\\/?u\\/[a-z0-9_\\-]{3,21})\\b[^\\n]{0,60}?\\b(?:is|was|are|were)\\s+(?:a\\s+|an\\s+|such\\s+a\\s+|the\\s+)?(?:fucking\\s+)?(?:creep|creepy|catfish|scammer|liar|predator|pedo|ghoster|time\\s?waster|troll|abusive|harasser|fake|weirdo|asshole|jerk|not\\s+to\\s+be\\s+trusted)\\b|\\b(?:beware\\s+of|avoid|block|report|do\\s?n'?t\\s+(?:message|msg|dm|reply\\s+to|trust|bother\\s+with))\\s+(?:the\\s+user\\s+)?(?:(?<![a-z0-9._%+\\-])@[a-z0-9_.\\-]{2,30}|\\/?u\\/[a-z0-9_\\-]{3,21})\\b"
  },
  // Rule 1 'calling out' intersecting rule 5. Publicly accusing another
  // member of copying is the calling-out behaviour rule 1 prohibits, and
  // simultaneously a genuine rule-5 report an admin wants to triage. Review,
  // never block - the accusation is often correct.
  {
    id: 'callout-plagiarism-accusation',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:(?:stole|copied|plagiari[sz]ed|ripped\\s+off)\\s+(?:my|our)\\s+(?:post|prompt|ad|listing|writing|words|text|bio|intro|description)|(?:my|this)\\s+(?:post|prompt|ad|writing)\\s+(?:was|got|is\\s+being)\\s+(?:stolen|copied|plagiari[sz]ed)|someone\\s+(?:is\\s+)?(?:stealing|copying)\\s+my\\s+(?:post|prompt|ad|writing|words))\\b"
  },
  // Rule 1's 'calling out' clause without a handle - the 'warning about a
  // guy who posts here' genre. Review: some are good-faith safety warnings,
  // others retaliation, and the regex cannot tell them apart.
  {
    id: 'callout-user-warning-generic',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:(?:this|that)\\s+(?:user|guy|girl|woman|man|person|poster|account|profile|writer)\\s+(?:is|was)\\s+(?:a\\s+|an\\s+)?(?:creep|creepy|catfish|scammer|liar|predator|pedo(?:phile)?|troll|abusive|harasser|fake|time\\s?waster|asshole)|beware\\s+of\\s+(?:the\\s+|a\\s+|this\\s+|that\\s+)(?:user|guy|girl|man|woman|person|poster|account|profile)|stay\\s+away\\s+from\\s+(?:the\\s+|this\\s+|that\\s+)(?:user|guy|girl|man|woman|person|poster|account)|do\\s?n'?t\\s+(?:message|msg|dm|reply\\s+to|write\\s+to|trust)\\s+(?:the\\s+|this\\s+|that\\s+)(?:user|guy|girl|man|woman|person|poster|account)|blocked\\s+and\\s+reported\\s+(?:him|her|them|that\\s+\\w+)|psa\\s*[:\\-]\\s*(?:avoid|beware|watch\\s+out|stay\\s+away|there'?s\\s+a))"
  },
  // Rule 1 'shaming'. Exclusion phrased as degradation of a body type rather
  // than as a stated preference. Review because the line between an allowed
  // preference and prohibited degradation is a human judgement.
  {
    id: 'exclusion-body-shaming',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:no\\s+(?:fat|obese|overweight|ugly|fugly)\\s+(?:chicks|girls|women|guys|men|dudes|people|bitches|hoes)|(?:fat|obese|ugly|fugly)\\s+(?:chicks|girls|women|guys|men|people)\\s+(?:need\\s+not\\s+apply|not\\s+welcome|do\\s?n'?t\\s+bother|will\\s+be\\s+ignored|stay\\s+away)|fatties|land\\s?whales?|ham\\s?planets?|fuglies|(?<!bump(?:ing)?\\s)uglies|do\\s?n'?t\\s+(?:bother|message\\s+me|reply)\\s+if\\s+(?:you'?re|ur|you\\s+are)\\s+(?:fat|obese|overweight|ugly|fugly))\\b"
  },
  // Rule 1 'shaming'. The 'no fats no femmes no asians' construction states
  // exclusion as a category of person rather than as a preference. Review,
  // not block - the site allows stated partner preferences.
  {
    id: 'exclusion-demographic-plural',
    category: 'harassment',
    severity: 'review',
    source: "\\bno\\s+(?:fats|fems|femmes|blacks|asians|indians|arabs|latinos|latinas|mexicans|jews|muslims|whites|browns)\\b"
  },
  // Rule 1. Organising other members into a pile-on. Review because a
  // good-faith 'please report this' about a genuine rule breaker lands here
  // too, and an admin wants to see that.
  {
    id: 'harassment-brigading',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:(?:everyone|everybody|you\\s+all|y'?all|we\\s+all)\\s+(?:should\\s+|needs?\\s+to\\s+|please\\s+)?(?:block|report|avoid|downvote)\\b|(?:mass|please)\\s+report\\s+(?:this|that|the)\\s+(?:user|guy|girl|person|poster|account|post|prompt)|let'?s\\s+(?:all\\s+)?report\\b|spread\\s+the\\s+word\\s+about\\s+(?:this|that)\\s+(?:user|guy|girl|person|poster|account))"
  },
  // Rule 1 'shaming'. Blanket contempt aimed at the site's other members
  // rather than at a fictional partner. Review only - insulting but
  // low-harm, and it shades into venting.
  {
    id: 'shaming-of-repliers',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:anyone|everyone|most\\s+(?:of\\s+)?(?:the\\s+)?(?:guys|men|women|people)|the\\s+(?:guys|men|women|people))\\s+(?:who\\s+)?(?:messages?|msgs?|dms?|pms?|writes?|replies|reply|posts?)\\s+(?:me|here|to\\s+me|on\\s+(?:this\\s+site|here))(?:\\s+(?:here|on\\s+(?:this\\s+site|here)))?\\s+(?:is|are)\\s+(?:a\\s+|all\\s+|just\\s+){0,2}(?:creeps?|losers?|pathetic|desperate|disgusting|trash|garbage|weirdos?|freaks?|illiterate|idiots?|morons?|perverts?)\\b"
  },
  // Rule 1. Anti-gay / anti-trans slurs. REVIEW rather than block because
  // all four are heavily used self-referentially and in reclaimed senses
  // here (sissy/faggot self-degradation, trans posters using 'tranny',
  // lesbians using 'dyke').
  {
    id: 'slur-lgbtq-any-form',
    category: 'harassment',
    severity: 'review',
    source: "(?<!\\b(?:call(?:ed|ing|s)?|i'?m|im|i\\s+am|as|being|be|treat\\s+me\\s+like|make\\s+me\\s+(?:your|his|her)|use\\s+me\\s+like|your|my|his|her|their|our|own\\s+personal)\\s(?:me\\s|him\\s|her\\s|us\\s|them\\s)?(?:a\\s|an\\s|such\\s+a\\s)?(?:dirty\\s|filthy\\s|nasty\\s|little\\s|white\\s|black\\s|worthless\\s|pathetic\\s|stupid\\s|dumb\\s|sissy\\s|butch\\s|femme\\s|proud\\s|total\\s|needy\\s|eager\\s|closeted\\s|good\\s|useless\\s|cock\\s?hungry\\s){0,2})\\b(?:f[a4]gg?[o0]ts?|f[a4]gg?y|trann(?:y|ies)|dykes?)\\b(?!(?:\\s+(?:slut|sub|subby|bottom|top|boy|girl|guy|gal|pig|whore|bitch|toy|here))?\\s*,?\\s+(?:here|seeking|looking\\s+for|searching|want(?:s|ing)?|available|checking\\s+in|reporting\\s+in|speaking|writing))"
  },
  // Rule 1. Slurs and ableist terms that are real but carry enough dated,
  // casual or race-play-vocabulary usage that a hard refuse would be wrong.
  // Queue for a human.
  {
    id: 'slur-misc-lower-tier',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:wops?|krauts?|honk(?:y|ies|eys?)\\b(?![\\s\\-]?tonk)|jigg?aboos?|gypp?ed|retard(?:s|ed)?|chinam[ae]n|japs|mudsharks?|coal\\s?burners?)\\b"
  },
  // Rule 1. Covers plain, leetspeak, letter-spaced and asterisk-censored
  // forms. Deliberately REVIEW: consensual race-play is legitimate here and
  // routinely contains this word in a self-directed frame, so a human
  // decides.
  {
    id: 'slur-nword-any-form',
    category: 'harassment',
    severity: 'review',
    source: "(?<!\\b(?:call(?:ed|ing|s)?|i'?m|im|i\\s+am|as|being|be|treat\\s+me\\s+like|make\\s+me\\s+(?:your|his|her)|use\\s+me\\s+like|your|my|his|her|their|our|own\\s+personal)\\s(?:me\\s|him\\s|her\\s|us\\s|them\\s)?(?:a\\s|an\\s|such\\s+a\\s)?(?:dirty\\s|filthy\\s|nasty\\s|little\\s|white\\s|black\\s|worthless\\s|pathetic\\s|stupid\\s|dumb\\s|sissy\\s|butch\\s|femme\\s|proud\\s|total\\s|needy\\s|eager\\s|closeted\\s|good\\s|useless\\s|cock\\s?hungry\\s){0,2})\\b(?:n[\\W_]{0,1}[i1!|*][\\W_]{0,1}g[\\W_]{0,1}g[\\W_]{0,1}(?:[e3*][\\W_]{0,1}r|[a@])s?|n\\*{2,4}[ae]r?s?)\\b(?!(?:\\s+(?:slut|sub|subby|bottom|top|boy|girl|guy|gal|pig|whore|bitch|toy|here))?\\s*,?\\s+(?:here|seeking|looking\\s+for|searching|want(?:s|ing)?|available|checking\\s+in|reporting\\s+in|speaking|writing))"
  },
  // Rule 1, threatening language. Real-world consequence threats: exposure,
  // doxxing, telling a spouse or employer. Review because blackmail/exposure
  // fantasy is a popular legitimate kink and the same sentence can be
  // either.
  {
    id: 'threat-real-world-exposure',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:i(?:'?ll|\\s+will|\\s+am\\s+going\\s+to|'?m\\s+going\\s+to|\\s+gonna)\\s+(?:expose|leak|post|share|screenshot|publish|forward|send)\\s+(?:your|ur)\\s+(?:nudes?|pics?|photos?|dms?|pms?|messages?|chats?|screenshots?|face|address|real\\s+name|identity|info)|dox+(?:x?ing|x?ed)?|i(?:'?ll|\\s+will)\\s+tell\\s+(?:your|ur)\\s+(?:wife|husband|boss|employer|family|mom|mum|dad|parents|church|kids)|report(?:ing)?\\s+(?:you|him|her)\\s+to\\s+(?:your|his|her)\\s+(?:job|work|employer|boss|school|university|college))\\b"
  },
  // Rule 1, threatening language with a real-world locus. Review, never
  // block: stalker and chase fantasy are mainstream kinks and these exact
  // sentences appear inside legitimate scene text.
  {
    id: 'threat-stalking-irl',
    category: 'harassment',
    severity: 'review',
    source: "\\b(?:i\\s+know\\s+where\\s+you\\s+(?:live|work|sleep)|i\\s+(?:have|got|found)\\s+your\\s+(?:ip|address|real\\s+name|phone\\s+number|workplace)|i\\s+traced\\s+your\\s+ip|i(?:'?ll|\\s+will)\\s+(?:find|hunt\\s+down|show\\s+up\\s+at|come\\s+to)\\s+(?:you|your\\s+house|your\\s+work)\\s+(?:irl|in\\s+real\\s+life|for\\s+real)|i(?:'?ll|\\s+will)\\s+(?:beat|hurt|fuck)\\s+you\\s+up\\s+(?:irl|in\\s+real\\s+life|for\\s+real))\\b"
  }
];

// ------------------------------------------------------------ derived pattern
//
// The birth-year signal is the one pattern that goes stale on a calendar: the
// set of birth years that imply a minor moves every January. Build it at load
// time rather than hardcoding it, so an old deploy degrades into slightly wide
// review noise instead of quietly waving through the current 17-year-olds.
const buildBirthYearPattern = now => {
  const year = now.getFullYear();
  const bornYears = [];
  for (let y = year - 17; y <= year + 2; y += 1) bornYears.push(y);
  const classYears = [];
  for (let y = year + 1; y <= year + 13; y += 1) classYears.push(y);
  const shortClass = classYears.map(y => String(y).slice(2));
  return (
    `\\bborn\\s+(?:in\\s+)?(?:${bornYears.join('|')})\\b` +
    `|\\bclass\\s+of\\s+(?:'?(?:${shortClass.join('|')})|${classYears.join('|')})\\b`
  );
};

const DERIVED_TABLE = [
  {
    id: 'underage-birth-year',
    category: 'underage',
    severity: 'review',
    source: buildBirthYearPattern(new Date())
  }
];

// Several patterns use lookbehind. Node 20 and every current browser handle it,
// but an ancient Safari would throw at compile time and take the whole bundle
// down with it. Drop what will not compile instead: the client check is a
// courtesy, functions/promptModerationFunctions.js is the authority.
const COMPILED = [];
const UNCOMPILABLE = [];
PATTERN_TABLE.concat(DERIVED_TABLE).forEach(entry => {
  try {
    COMPILED.push({
      id: entry.id,
      category: entry.category,
      severity: entry.severity,
      regex: new RegExp(entry.source, 'i')
    });
  } catch (error) {
    UNCOMPILABLE.push(entry.id);
  }
});

// ---------------------------------------------------------------------- api

const firstMatch = (regex, haystack) => {
  const found = haystack.match(regex);
  return found ? found[0] : null;
};

const truncate = value =>
  value.length <= MATCH_ECHO_LIMIT
    ? value
    : `${value.slice(0, MATCH_ECHO_LIMIT - 1)}…`;

// How much of the body is scanned. Nothing caps the length of prompt text -
// promptValidation() in the security rules only bounds the title - and a few of
// the domain patterns cost roughly quadratic time, so an unbounded body is a
// way to freeze the author's own tab and burn Cloud Function seconds (measured:
// 100k characters of "a-a-a-" took 4.4s, while 90k of real prose took 21ms).
// 12k characters is about four times the longest genuine prompt. Title and tags
// are always scanned whole, so nothing can be pushed out of range by padding.
const MAX_TEXT_SCAN = 12000;

// Takes the prompt's PLAIN text - callers decode draft-js first, because the
// two sides store it differently (EditorState on the client, a stringified raw
// content state in Firestore).
const checkPromptRules = promptLike => {
  const source = promptLike || {};
  const rawText = typeof source.text === 'string' ? source.text : '';
  const fields = {
    title: normalizeForMatching(
      typeof source.title === 'string' ? source.title : ''
    ),
    text: normalizeForMatching(rawText.slice(0, MAX_TEXT_SCAN)),
    tags: normalizeForMatching(
      Array.isArray(source.tags) ? source.tags.join(', ') : ''
    )
  };

  // Each field on its own AND all three joined.
  //
  // Joined, because putting the age in the title and the request in the body is
  // the obvious split and neither half trips a pattern alone.
  //
  // Individually, because several of the underage patterns end in an anchored
  // lookahead - the guard that stops "a 15 year old crush" being refused. A
  // title that ENDS "...sixteen years old" satisfies that anchor on its own but
  // not once the body has been concatenated after it, so scanning only the
  // joined text silently downgrades those patterns to review.
  //
  // Field-scoped views come first so the most specific attribution wins.
  const views = [
    { field: 'title', value: fields.title },
    { field: 'tags', value: fields.tags },
    { field: 'text', value: fields.text },
    { field: 'prompt', value: `${fields.title}\n${fields.text}\n${fields.tags}` }
  ];

  // ...and each of those again with accents stripped. Skipped when it changes
  // nothing, which is every all-ASCII prompt, so this is usually free.
  const scanned = [];
  views.forEach(view => {
    scanned.push(view);
    const folded = stripDiacritics(view.value);
    if (folded !== view.value) scanned.push({ field: view.field, value: folded });
  });

  const violations = [];
  COMPILED.forEach(entry => {
    let hit = null;
    scanned.some(view => {
      const match = firstMatch(entry.regex, view.value);
      if (match) hit = { field: view.field, match };
      return !!match;
    });
    if (!hit) return;
    violations.push({
      id: entry.id,
      category: entry.category,
      severity: entry.severity,
      rule: RULES[entry.category].number,
      field: hit.field,
      match: truncate(hit.match)
    });
  });

  let verdict = 'clean';
  if (violations.some(violation => violation.severity === 'block')) {
    verdict = 'block';
  } else if (violations.length > 0) {
    verdict = 'review';
  }

  return { version: RULES_ENGINE_VERSION, verdict, violations };
};

// One line per broken RULE rather than per pattern - three underage patterns
// firing is still one thing for the author to fix. The matched text is echoed
// back so someone caught by a false positive can see which words to change;
// with this many patterns, a bare "your post violates the rules" is what turns
// a false positive into a lost user.
const formatViolationsForUser = violations => {
  const blocking = (violations || []).filter(
    violation => violation.severity === 'block'
  );
  if (blocking.length === 0) return '';

  const byCategory = {};
  blocking.forEach(violation => {
    if (!byCategory[violation.category]) byCategory[violation.category] = [];
    if (byCategory[violation.category].indexOf(violation.match) === -1) {
      byCategory[violation.category].push(violation.match);
    }
  });

  const lines = Object.keys(byCategory).map(category => {
    const rule = RULES[category];
    const quoted = byCategory[category]
      .slice(0, 3)
      .map(match => `"${match}"`)
      .join(', ');
    return `Rule ${rule.number} (${rule.title}) - please remove or reword: ${quoted}`;
  });

  return [
    "This prompt can't be published because it breaks the site rules:",
    lines.join('\n'),
    'See eropenpal.com/rules. You can still save it as a draft.'
  ].join('\n');
};

module.exports = {
  RULES_ENGINE_VERSION,
  RULES,
  checkPromptRules,
  formatViolationsForUser,
  normalizeForMatching,
  patternIds: () => COMPILED.map(entry => entry.id),
  uncompilablePatternIds: () => UNCOMPILABLE.slice()
};
